import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { aiUsageService } from '../config/api';

const AiUsage = () => {
  const [usage, setUsage] = useState(null);
  const [billing, setBilling] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingRange, setBillingRange] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = Date.now();
      const startEpochMs = now - billingRange * 24 * 60 * 60 * 1000;

      const [usageData, billingData, recentData] = await Promise.all([
        aiUsageService.getUserUsage(),
        aiUsageService.getBilling(startEpochMs, now),
        aiUsageService.getRecentUsage(50),
      ]);

      setUsage(usageData);
      setBilling(billingData);
      setRecent(recentData);
    } catch (err) {
      setError(err.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [billingRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCost = (cost) => `$${(cost || 0).toFixed(4)}`;
  const formatTokens = (tokens) => (tokens || 0).toLocaleString();
  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const date = new Date(ts);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="alert alert-error">
          <span>{error}</span>
          <button className="btn btn-sm btn-ghost" onClick={fetchData}>Retry</button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">AI Usage</h1>

        {/* Section A: Usage Summary */}
        <div className="stats stats-vertical lg:stats-horizontal shadow w-full bg-base-100">
          <div className="stat">
            <div className="stat-title">Total Tokens</div>
            <div className="stat-value text-primary">{formatTokens(usage?.grandTotalTokens)}</div>
            <div className="stat-desc">{usage?.requestCount || 0} requests</div>
          </div>
          <div className="stat">
            <div className="stat-title">Prompt Tokens</div>
            <div className="stat-value text-secondary">{formatTokens(usage?.totalPromptTokens)}</div>
          </div>
          <div className="stat">
            <div className="stat-title">Completion Tokens</div>
            <div className="stat-value text-accent">{formatTokens(usage?.totalCompletionTokens)}</div>
          </div>
          <div className="stat">
            <div className="stat-title">Estimated Cost</div>
            <div className="stat-value">{formatCost(usage?.estimatedTotalCost)}</div>
          </div>
        </div>

        {/* Section B: Billing Breakdown */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="card-title">Billing Breakdown</h2>
              <div className="join">
                {[7, 30, 90].map((days) => (
                  <button
                    key={days}
                    className={`join-item btn btn-sm ${billingRange === days ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setBillingRange(days)}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>

            {billing && (
              <div className="text-sm text-base-content/60 mb-2">
                Period: {formatTimestamp(billing.periodStart)} — {formatTimestamp(billing.periodEnd)}
                {' | '}Total: {formatCost(billing.totalCost)} ({formatTokens(billing.totalTokens)} tokens, {billing.requestCount} requests)
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* By Model */}
              <div>
                <h3 className="font-semibold mb-2">By Model</h3>
                <div className="overflow-x-auto">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Model</th>
                        <th className="text-right">Tokens</th>
                        <th className="text-right">Cost</th>
                        <th className="text-right">Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(billing?.modelBreakdown || []).length === 0 ? (
                        <tr><td colSpan={4} className="text-center text-base-content/40">No data</td></tr>
                      ) : (
                        billing.modelBreakdown.map((m, i) => (
                          <tr key={i}>
                            <td><span className="badge badge-ghost badge-sm">{m.modelName}</span></td>
                            <td className="text-right">{formatTokens(m.tokens)}</td>
                            <td className="text-right">{formatCost(m.cost)}</td>
                            <td className="text-right">{m.requests}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* By Agent */}
              <div>
                <h3 className="font-semibold mb-2">By Agent</h3>
                <div className="overflow-x-auto">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th className="text-right">Tokens</th>
                        <th className="text-right">Cost</th>
                        <th className="text-right">Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(billing?.agentBreakdown || []).length === 0 ? (
                        <tr><td colSpan={4} className="text-center text-base-content/40">No data</td></tr>
                      ) : (
                        billing.agentBreakdown.map((a, i) => (
                          <tr key={i}>
                            <td><span className="badge badge-ghost badge-sm">{a.agentName}</span></td>
                            <td className="text-right">{formatTokens(a.tokens)}</td>
                            <td className="text-right">{formatCost(a.cost)}</td>
                            <td className="text-right">{a.requests}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section C: Recent Activity */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title">Recent Activity</h2>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Agent</th>
                    <th>Model</th>
                    <th>Provider</th>
                    <th className="text-right">Prompt</th>
                    <th className="text-right">Completion</th>
                    <th className="text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-base-content/40">No recent activity</td></tr>
                  ) : (
                    recent.map((r) => (
                      <tr key={r.id || r.requestId}>
                        <td className="whitespace-nowrap">{formatTimestamp(r.timestamp)}</td>
                        <td><span className="badge badge-ghost badge-sm">{r.agentName}</span></td>
                        <td><span className="badge badge-outline badge-sm">{r.modelName}</span></td>
                        <td>{r.providerType}</td>
                        <td className="text-right">{formatTokens(r.promptTokens)}</td>
                        <td className="text-right">{formatTokens(r.completionTokens)}</td>
                        <td className="text-right">{formatCost(r.estimatedCost)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AiUsage;
